import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Collapse, IconButton, Chip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { AutoAwesome } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TicketList from '../TicketList';
import { getBreakdownNodeType, renderBreakdownHierarchy } from '../strategistAgentUtils';
import '../BreakdownPreview.scss';

const RequirementHistoryItem = ({ requirement, breakdown, tickets, createdAt, totalTicketCount }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState([]);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (textRef.current && !expanded) {
      const element = textRef.current;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 5;
      setIsTruncated(element.scrollHeight > maxHeight);
    } else {
      setIsTruncated(false);
    }
  }, [requirement, expanded]);

  const getItemPath = (node, level = 0, parentType = null, index = 0) => {
    return `${level}-${index}-${node.title || 'item'}`;
  };

  const handleAccordionChange = (nodeId) => {
    setExpandedItems((prev) =>
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]
    );
  };

  const transformedTickets = useMemo(() => {
    if (!tickets) return [];

    // Flat array format from history API: [{ id, title, ticket_number, parent_ticket_id, ... }]
    if (Array.isArray(tickets)) {
      if (tickets.length === 0) return [];

      // Build tree from flat array using parent_ticket_id
      const map = {};
      tickets.forEach((ticket) => {
        map[ticket.id] = {
          id: ticket.id,
          ticketNumber: ticket.ticket_number || ticket.id,
          title: ticket.title,
          type: ticket.task_type || 'Task',
          priority: ticket.priority || 'Medium',
          children: [],
        };
      });

      const roots = [];
      tickets.forEach((ticket) => {
        if (ticket.parent_ticket_id && map[ticket.parent_ticket_id]) {
          map[ticket.parent_ticket_id].children.push(map[ticket.id]);
        } else {
          roots.push(map[ticket.id]);
        }
      });

      return roots;
    }

    // Legacy nested object format: { epic: {...}, task: {...} }
    if (typeof tickets !== 'object') return [];

    const flattenTickets = (node) => {
      if (!node?.id || !node?.title) return [];
      const ticket = {
        id: node.id,
        ticketNumber: node.ticket_number || node.id,
        title: node.title,
        type: node.task_type || 'Task',
        priority: node.priority || 'Medium',
        children: [],
      };
      const children = [];
      if (node.features) node.features.forEach((f) => children.push(...flattenTickets(f)));
      if (node.tasks) node.tasks.forEach((t) => children.push(...flattenTickets(t)));
      if (node.subtasks) node.subtasks.forEach((s) => children.push(...flattenTickets(s)));
      ticket.children = children;
      return [ticket];
    };
    
    if (tickets.epic || tickets.task) {
      return flattenTickets(tickets.epic || tickets.task);
    }
    
    return [];
  }, [tickets]);

  const getPriorityColor = (priority) => {
    const pName = typeof priority === 'object' ? priority.name : priority;
    switch (pName?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const renderTreeItem = (node, level = 0, parentType = null, index = 0, context = null) => {
    if (!node) return null;

    const nodeId = getItemPath(node, level, parentType, index);
    const nodeType = getBreakdownNodeType(node, level, parentType, context);
    const nodeTitle = node.title || 'Untitled';
    const nodeDescription = node.description || '';

    const hasChildren = (node.features?.length > 0) || (node.tasks?.length > 0) || (node.subtasks?.length > 0);
    const isExpanded = expandedItems.includes(nodeId);

    return (
      <Box key={nodeId} className="breakdown-tree-item" data-level={level} style={{ marginLeft: `${level * 5}px` }}>
        <Paper elevation={level === 0 ? 2 : 1} className="breakdown-tree-item-paper" data-level={level} data-type={nodeType}>
          <Box 
            className="breakdown-tree-item-content"
            onClick={hasChildren ? () => handleAccordionChange(nodeId) : undefined}
            style={hasChildren ? { cursor: 'pointer' } : {}}
          >
            <Box className="breakdown-tree-item-left">
              <Box className="breakdown-tree-item-header">
                <Box className="breakdown-tree-item-header-left">
                  <Chip
                    label={t(`agent.breakdown.${nodeType}`)}
                    size="small"
                    className={`breakdown-tree-item-type-chip breakdown-tree-item-type-chip-${nodeType}`}
                  />
                  <Typography variant="h6" className="breakdown-tree-item-title" data-level={level}>
                    {nodeTitle}
                  </Typography>
                </Box>
              </Box>

              {nodeDescription && (
                <Typography variant="body2" color="text.secondary" className="breakdown-tree-item-description">
                  {nodeDescription}
                </Typography>
              )}

              {node.priority && (
                <Box className="breakdown-tree-item-chips">
                  <Chip 
                    label={`${t('agent.breakdown.priority')}: ${typeof node.priority === 'object' ? node.priority.name : node.priority}`} 
                    size="small" 
                    variant="outlined"
                    color={typeof node.priority === 'object' && node.priority.colour ? undefined : getPriorityColor(node.priority)}
                    sx={typeof node.priority === 'object' && node.priority.colour ? { borderColor: node.priority.colour, color: node.priority.colour } : undefined}
                  />
                </Box>
              )}
            </Box>

            <Box className="breakdown-tree-item-actions">
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccordionChange(nodeId);
                  }}
                  className="breakdown-tree-item-action-button"
                >
                  <ExpandMore className="breakdown-tree-item-expand-icon" data-expanded={isExpanded} />
                </IconButton>
              )}
            </Box>
          </Box>

          {isExpanded && hasChildren && (
            <Box className="breakdown-tree-item-children">
              {node.features?.map((feature, idx) => renderTreeItem(feature, level + 1, nodeType, idx))}
              {node.tasks?.map((task, idx) => renderTreeItem(task, level + 1, nodeType, idx))}
              {node.subtasks?.map((subtask, idx) => renderTreeItem(subtask, level + 1, nodeType, idx))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Paper elevation={0} className="requirement-history-item">
      <Box className="requirement-history-item-header" onClick={handleToggle}>
        <Box className="requirement-history-item-header-left">
          <IconButton size="small" className="requirement-history-expand-button">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Box className="requirement-history-item-preview-section">
            <Typography 
              ref={textRef}
              variant="body2" 
              className={`requirement-history-item-preview ${expanded ? 'requirement-history-item-preview-expanded' : ''}`}
            >
              {requirement?.trim()}
            </Typography>
            {isTruncated && !expanded && (
              <Typography variant="body2" className="requirement-history-ellipsis">
                ...
              </Typography>
            )}
            {createdAt && (
              <Typography variant="caption" className="requirement-history-item-date">
                {createdAt}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className="requirement-history-item-stats">
          <Chip
            label={`${totalTicketCount ?? transformedTickets.length} ${t('agent.history.tickets')}`}
            size="small"
            className="requirement-history-stat-chip"
          />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className="requirement-history-item-content">
          <Box className="requirement-history-section">
            <Box className="agent-section-header">
              <Box className="agent-section-header-content">
                <AutoAwesome className="agent-section-header-icon" />
                <Typography variant="h6" className="agent-section-header-title">
                  {t('agent.history.breakdownSummary')}
                </Typography>
              </Box>
            </Box>
            <Box className="requirement-history-breakdown">
              {breakdown && typeof breakdown === 'object' && Object.keys(breakdown).length > 0 && !breakdown.error && !breakdown.parseError ? (
                <Box className="breakdown-preview-items-container">
                  {renderBreakdownHierarchy(breakdown, renderTreeItem)}
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" className="requirement-history-no-data">
                  {t('agent.history.noBreakdownGenerated')}
                </Typography>
              )}
            </Box>
          </Box>

          {transformedTickets.length > 0 && (
            <Box className="requirement-history-section">
              <Box className="agent-section-header">
                <Box className="agent-section-header-content">
                  <AutoAwesome className="agent-section-header-icon" />
                  <Typography variant="h6" className="agent-section-header-title">
                    {t('agent.history.generatedTickets')}
                  </Typography>
                </Box>
              </Box>
              <Box className="requirement-history-tickets">
                <TicketList tickets={transformedTickets} hideHeader={true} />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default RequirementHistoryItem;